import { TWO_FA_PROMPT_MESSAGE, TWO_FA_PROMPT_PREFIXES } from "../constants/payment";
import type { LogMessage, PaymentLog, PaymentLogEntry, PaymentProgressResult, Prompt2faCallback, RealTimeLogCallback } from "../types/payment";
import { PaymentResultStatus } from "../types/payment";
import type { LogGroup } from "../types/scraping";
import { formatCurrency, formatLogTime } from "./format";

const PROCESSING_INVOICE_KEY = "payment.processingInvoice";

const extractTwoFaPromptFromLogs = (logEntries: LogMessage[]): Prompt2faCallback | null => {
  const lastEntry = logEntries[logEntries.length - 1];
  const twoFaMatch = (lastEntry?.key ?? "").match(new RegExp(`^(${TWO_FA_PROMPT_PREFIXES.join("|")})(.+)$`));
  if (!twoFaMatch) return null;
  return { requestId: twoFaMatch[2], message: TWO_FA_PROMPT_MESSAGE };
};

const restoreProcessedInvoiceIds = (processedInvoiceIds: Set<number>, entries: PaymentLogEntry[] | undefined): void => {
  processedInvoiceIds.clear();
  for (const entry of entries ?? []) {
    if (entry.invoiceId != null) processedInvoiceIds.add(entry.invoiceId);
  }
};

const buildRestoredPaymentState = (data: PaymentLog): { logEntries: LogMessage[]; twoFaPrompt: Prompt2faCallback | null } => {
  const logEntries = (data.messages ?? []).map((msg) => ({
    key: msg.key ?? "",
    params: msg.params ?? undefined,
    ts: new Date(msg.createdAt).getTime(),
    timestamp: formatLogTime(msg.createdAt),
  }));
  return { logEntries, twoFaPrompt: extractTwoFaPromptFromLogs(logEntries) };
};

const buildResultMap = (payResults: PaymentLogEntry[] | null): Map<string, boolean> => {
  const resultMap = new Map<string, boolean>();
  if (payResults) {
    for (const r of payResults) {
      const key = `${r.addressName} / ${r.providerName} (${formatCurrency(r.amount)})`;
      resultMap.set(key, r.success === PaymentResultStatus.SUCCESS);
    }
  }
  return resultMap;
};

const reduceGroup = (groups: LogGroup[], currentGroup: LogGroup | null, entry: LogMessage, resultMap: Map<string, boolean>): LogGroup | null => {
  const msg = entry.key ?? "";
  if (msg === PROCESSING_INVOICE_KEY) {
    if (currentGroup) groups.push(currentGroup);
    const p = entry.params ?? {};
    const header = `${p.addressName ?? ""} / ${p.providerName ?? ""} (${formatCurrency(p.amount)})`;
    return { header, logs: [entry], result: resultMap.get(header) };
  }
  if (!currentGroup) {
    if (!groups.length || groups[0].header !== "Bank Login") {
      groups.push({ header: "Bank Login", logs: [], result: undefined });
    }
    groups[0].logs.push(entry);
    return null;
  }
  currentGroup.logs.push(entry);
  return currentGroup;
};

const groupLogEntries = (entries: LogMessage[], payResults: PaymentLogEntry[] | null): LogGroup[] => {
  const resultMap = buildResultMap(payResults);
  const groups: LogGroup[] = [];
  let currentGroup: LogGroup | null = null;

  for (const entry of entries) {
    currentGroup = reduceGroup(groups, currentGroup, entry, resultMap);
  }
  if (currentGroup) groups.push(currentGroup);

  return groups;
};

const createPaymentLogEntry = (result: PaymentProgressResult): PaymentLogEntry => ({
  ...result,
  id: 0,
  paymentLogId: 0,
  createdAt: "",
  messages: [],
});

const createLogMessage = (input: RealTimeLogCallback): LogMessage => ({
  key: input.key,
  params: input.params,
  ts: input.timestamp,
  timestamp: formatLogTime(input.timestamp),
});

const translateLogMessage = (
  t: (key: string, options?: Record<string, unknown>) => string,
  entry: Pick<RealTimeLogCallback, "key" | "params">,
): string => (entry.key ? t(entry.key, { ...entry.params }) : "");

export {
  PROCESSING_INVOICE_KEY,
  buildResultMap,
  groupLogEntries,
  createPaymentLogEntry,
  createLogMessage,
  translateLogMessage,
  buildRestoredPaymentState,
  restoreProcessedInvoiceIds,
};
